/**
 * Runtime Schema Inference for Script Data Interfaces
 *
 * Parses `export interface Data extends BehaviorData { ... }` from script
 * source and produces FieldSchema[] for the editor UI.
 *
 * This is a lightweight regex-based parser — no TypeScript AST at runtime.
 * It handles the restricted subset of types allowed in user scripts:
 * - number, string, boolean primitives
 * - string literal unions (enum-like)
 * - Optional properties (?)
 * - JSDoc tags: @default, @step, @label, @hidden, @description
 */

import type { FieldSchema, FieldType } from '../scenedata/schemas/types';

/**
 * Extract the Data interface body from script source.
 * Returns the text between `{` and `}` of `export interface Data extends BehaviorData { ... }`.
 */
function extractDataInterfaceBody(source: string): string | null {
    // Match `export interface Data extends BehaviorData {`
    const startPattern = /export\s+interface\s+Data\s+extends\s+BehaviorData\s*\{/;
    const match = startPattern.exec(source);
    if (!match) return null;

    // Find matching closing brace
    let depth = 1;
    let i = match.index + match[0].length;
    const start = i;

    while (i < source.length && depth > 0) {
        if (source[i] === '{') depth++;
        if (source[i] === '}') depth--;
        if (depth > 0) i++;
    }

    return source.slice(start, i);
}

/**
 * Parse a single property line from the interface body.
 * Handles: `propertyName?: TypeAnnotation;` with optional JSDoc above.
 */
interface ParsedProperty {
    key: string;
    typeText: string;
    optional: boolean;
    jsdoc: Map<string, string>;
}

function camelToTitle(str: string): string {
    return str
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, s => s.toUpperCase())
        .trim();
}

/**
 * Parse JSDoc comment block into tag map.
 * Handles: @default value, @step 0.1, @label "Custom Label", @hidden, @description text
 */
function parseJSDoc(commentBlock: string): Map<string, string> {
    const tags = new Map<string, string>();

    // Extract description (text before any @tag)
    const descMatch = commentBlock.match(/\/\*\*\s*([\s\S]*?)(?=\s*@|\s*\*\/)/);
    if (descMatch) {
        const desc = descMatch[1]
            .replace(/^\s*\*\s?/gm, '')
            .trim();
        if (desc) tags.set('description', desc);
    }

    // Extract @tags
    const tagPattern = /@(\w+)\s*([\s\S]*?)(?=\s*@\w|\s*\*\/)/g;
    let tagMatch;
    while ((tagMatch = tagPattern.exec(commentBlock)) !== null) {
        const tagName = tagMatch[1];
        const tagValue = tagMatch[2]
            .replace(/^\s*\*\s?/gm, '')
            .trim();
        tags.set(tagName, tagValue);
    }

    return tags;
}

/**
 * Parse properties from the interface body text.
 */
function parseProperties(body: string): ParsedProperty[] {
    const properties: ParsedProperty[] = [];

    // Split into chunks where each property may be preceded by a JSDoc comment
    // We process line by line, accumulating JSDoc blocks
    const lines = body.split('\n');
    let currentJSDoc = '';
    let inJSDoc = false;

    for (const line of lines) {
        const trimmed = line.trim();

        // Start of JSDoc
        if (trimmed.startsWith('/**')) {
            inJSDoc = true;
            currentJSDoc = trimmed;
            if (trimmed.endsWith('*/')) {
                inJSDoc = false;
            }
            continue;
        }

        // Inside JSDoc
        if (inJSDoc) {
            currentJSDoc += '\n' + trimmed;
            if (trimmed.includes('*/')) {
                inJSDoc = false;
            }
            continue;
        }

        // Property line: `key?: Type;` or `key: Type;`
        // Also handle `'@type': 'Something';` which we skip
        const propMatch = trimmed.match(/^(\w+)(\??)\s*:\s*(.+?)\s*;?\s*$/);
        if (propMatch) {
            const key = propMatch[1];
            const optional = propMatch[2] === '?';
            const typeText = propMatch[3];

            const jsdoc = currentJSDoc ? parseJSDoc(currentJSDoc) : new Map<string, string>();
            currentJSDoc = '';

            properties.push({ key, typeText, optional, jsdoc });
            continue;
        }

        // Quoted property: `'@type': 'Something';` — skip these
        if (trimmed.match(/^['"]@type['"]/) || trimmed.match(/^['"]@/)) {
            currentJSDoc = '';
            continue;
        }

        // Empty or comment line — reset JSDoc if not currently in one
        if (!inJSDoc) {
            // Keep JSDoc if the next line might be a property
            if (trimmed === '' || trimmed.startsWith('//')) {
                // Don't reset — JSDoc may be followed by blank lines
            }
        }
    }

    return properties;
}

/**
 * Map a TypeScript type string to a FieldType.
 */
function resolveFieldType(typeText: string, key: string): { fieldType: FieldType; options?: string[] } {
    const t = typeText.trim();

    // number
    if (t === 'number') return { fieldType: 'number' };

    // boolean
    if (t === 'boolean') return { fieldType: 'boolean' };

    // string
    if (t === 'string') {
        // Heuristic: if key contains "color", use color picker
        if (key.toLowerCase().includes('color')) return { fieldType: 'color' };
        return { fieldType: 'string' };
    }

    // String literal union: 'a' | 'b' | 'c'
    const literalUnionMatch = t.match(/^['"]([^'"]+)['"](\s*\|\s*['"][^'"]+['"])*$/);
    if (literalUnionMatch) {
        const options = t.split('|').map(s => s.trim().replace(/^['"]|['"]$/g, ''));
        return { fieldType: 'enum', options };
    }

    // Vec3 tuple type: [number, number, number]
    if (/^\[number\s*,\s*number\s*,\s*number\]$/.test(t)) {
        return { fieldType: 'vec3' };
    }
    // Legacy Vec3 object type: { x: number; y: number; z: number }
    if (t.includes('x:') && t.includes('y:') && t.includes('z:')) {
        return { fieldType: 'vec3' };
    }

    // Fallback to string
    return { fieldType: 'string' };
}

function stripQuotes(value: string | undefined): string | undefined {
    if (!value) return value;
    if ((value.startsWith('"') && value.endsWith('"')) ||
        (value.startsWith("'") && value.endsWith("'"))) {
        return value.slice(1, -1);
    }
    return value;
}

function parseDefaultValue(value: string): unknown {
    value = value.trim();

    // Remove quotes for strings
    if ((value.startsWith('"') && value.endsWith('"')) ||
        (value.startsWith("'") && value.endsWith("'"))) {
        return value.slice(1, -1);
    }

    // Boolean
    if (value === 'true') return true;
    if (value === 'false') return false;

    // Number
    const num = parseFloat(value);
    if (!isNaN(num)) return num;

    // Object/array literals
    if (value.startsWith('{') || value.startsWith('[')) {
        try {
            const jsonValue = value
                .replace(/(\w+):/g, '"$1":')
                .replace(/'/g, '"');
            return JSON.parse(jsonValue);
        } catch {
            // fall through
        }
    }

    return value;
}

/**
 * Infer a FieldSchema[] from a script's TypeScript source code.
 * Returns empty array if no Data interface is found.
 */
export function inferScriptSchema(source: string): FieldSchema[] {
    const body = extractDataInterfaceBody(source);
    if (!body) return [];

    const properties = parseProperties(body);
    const fields: FieldSchema[] = [];

    for (const prop of properties) {
        // Skip @type property
        if (prop.key === '@type') continue;

        // Skip hidden/deprecated
        if (prop.jsdoc.has('hidden') || prop.jsdoc.has('deprecated')) continue;

        const { fieldType, options } = resolveFieldType(prop.typeText, prop.key);

        // Common props shared by all field variants (only include optional keys when set)
        const base: { key: string; label: string; default?: unknown; description?: string; optional?: boolean } = {
            key: prop.key,
            label: stripQuotes(prop.jsdoc.get('label')) || camelToTitle(prop.key),
        };
        if (prop.jsdoc.has('default')) base.default = parseDefaultValue(prop.jsdoc.get('default')!);
        if (prop.jsdoc.has('description')) base.description = prop.jsdoc.get('description');

        // Mark optional when prop is `?` and has no default value
        // (number fields always get a default of 0, so they're never truly optional)
        if (prop.optional && base.default === undefined && fieldType !== 'number') {
            base.optional = true;
        }

        // Build the correctly-typed variant per fieldType
        switch (fieldType) {
            case 'number': {
                const step = prop.jsdoc.has('step')
                    ? parseFloat(prop.jsdoc.get('step')!)
                    : 0.01;
                fields.push({ ...base, type: 'number', step, default: base.default ?? 0 });
                break;
            }
            case 'enum':
                fields.push({ ...base, type: 'enum', options: options! });
                break;
            case 'boolean':
                fields.push({ ...base, type: 'boolean' });
                break;
            case 'color':
                fields.push({ ...base, type: 'color' });
                break;
            case 'vec3':
                fields.push({ ...base, type: 'vec3' });
                break;
            case 'string':
            default:
                fields.push({ ...base, type: 'string' });
                break;
        }
    }

    return fields;
}
